import React, { useCallback, useEffect, useState } from 'react';
import LoginModal from '@/components/LoginModal/LoginModal';
import { debug } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_LOGGED_OUT_WELCOME_SCREEN_VIEWED,
  EVENT_LOGGED_OUT_WELCOME_GET_STARTED_CLICKED,
  EVENT_LOGGED_OUT_WELCOME_CONTINUE_EXPLORE_CLICKED,
} from '@/lib/analyticsEvents';

interface WelcomeScreenProps {
  onExplore?: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onExplore }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    WebAnalytics.track(EVENT_LOGGED_OUT_WELCOME_SCREEN_VIEWED);
    debug('[WelcomeScreen] Welcome screen viewed');
  }, []);

  const handleGetStarted = useCallback(() => {
    WebAnalytics.track(EVENT_LOGGED_OUT_WELCOME_GET_STARTED_CLICKED);
    debug('[WelcomeScreen] Get Started tapped');
    setShowLoginModal(true);
  }, []);

  const handleContinueToExplore = useCallback(() => {
    WebAnalytics.track(EVENT_LOGGED_OUT_WELCOME_CONTINUE_EXPLORE_CLICKED);
    debug('[WelcomeScreen] Continue to Explore tapped');
    onExplore?.();
  }, [onExplore]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" data-testid="welcome-screen">
      {/* Collage background — full-bleed behind everything */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <img
          src="/welcome/welcome_graphic.webp"
          alt=""
          className="h-[77.4%] w-full object-cover object-top"
        />
      </div>

      {/* Gradient fade: transparent top → white bottom (matches Figma "Fade" node) */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,255,255,0) 13%, rgba(255,255,255,0) 52%, rgba(255,255,255,0.6) 62%, white 72%)',
        }}
      />

      {/* Bottom branding + CTAs — pushed to bottom */}
      <div
        className="relative z-10 mt-auto flex w-full flex-col items-center px-2"
        style={{
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex w-full flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-4">
            <img
              src="/welcome/wordmark_logo.png"
              alt="Globe Thrivers"
              className="h-[46px] w-[320px] max-w-[90vw] object-contain"
            />
            <h1 className="w-[264px] max-w-[90vw] text-center font-lora text-[22px] font-semibold leading-[28px] tracking-[0.44px] text-[#252528]">
              Turn travel inspiration into a real trip
            </h1>
          </div>

          <button
            type="button"
            onClick={handleGetStarted}
            className="w-[352px] max-w-[calc(100%-16px)] rounded-[20px] bg-[#252528] px-4 py-3 text-center font-manrope text-base font-normal leading-[22px] tracking-[0.32px] text-white shadow-[0_0_20px_rgba(0,0,0,0.15)]"
            data-testid="welcome-get-started-btn"
          >
            Get Started
          </button>

          {onExplore && (
            <button
              type="button"
              onClick={handleContinueToExplore}
              className="text-center font-manrope text-base font-normal leading-[22px] tracking-[0.32px] text-[#252528] underline"
              data-testid="welcome-continue-explore-btn"
            >
              Continue to Explore
            </button>
          )}
        </div>
      </div>

      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          initialMode="signup"
          variant="figma-auth"
        />
      )}
    </div>
  );
};

export default WelcomeScreen;
